import * as React from "react";
import * as ReactQuery from "react-query";

import {
  GenericDataSource_CapableOfBeingIntersected,
  GraphqlWrappedData,
} from "../../../Common";
import { useDataSourceMyBagTotalsController } from "../data-source--mybag-totals-provider";

export interface UseGenericDataPortForMyBagConsumer {
  dataUpdatedAt: GenericDataSource_CapableOfBeingIntersected<any>["dataUpdatedAt"];
  errorUpdatedAt: GenericDataSource_CapableOfBeingIntersected<any>["errorUpdatedAt"];
}
export interface UseGenericDataPortForMyBagController {
  portError: <K extends string, T>(data: GraphqlWrappedData<K, T>) => void;
  portSuccess: () => void;
  useMutationOrQueryOnSuccess: () => void;
  ignoreMyCurrentStateFactory: (
    mutation: ReactQuery.UseMutationResult<any, any, any, any>
  ) => GenericDataSource_CapableOfBeingIntersected<any>["ignoreMyCurrentState"];
}
const OUT_STOCK_ERROR = "The requested qty is not available";
const OUT_STOCK_UPDATE = "This product is out of stock";
const OUT_OF_STOCK_GENERIC_STARTING = "Could not update the product";
export const useGenericDataPortForMyBag = (): [
  UseGenericDataPortForMyBagConsumer,
  UseGenericDataPortForMyBagController
] => {
  const { invalidate: dataSourceMyBagTotals_invalidate } =
    useDataSourceMyBagTotalsController();

  const [dataUpdatedAt, setDataUpdatedAt] = React.useState(0);
  const [errorUpdatedAt, setErrorUpdatedAt] = React.useState(0);

  const portError = React.useCallback(
    <K extends string, T>(data: GraphqlWrappedData<K, T>) => {
      if (data.errors) {
        const firstErrorMessage =
          (data.errors.length > 0 ? data.errors[0].message : "") ||
          "Unknown Error";

        const isThisAnOutOfStockError =
          firstErrorMessage.includes(OUT_STOCK_ERROR) ||
          firstErrorMessage.includes(OUT_STOCK_UPDATE) ||
          firstErrorMessage.includes(OUT_OF_STOCK_GENERIC_STARTING);

        if (isThisAnOutOfStockError) {
          setErrorUpdatedAt((previous) => {
            if (Date.now() > previous) {
              return Date.now();
            } else {
              return previous;
            }
          });
        }

        throw new Error(firstErrorMessage);
      }
    },
    [setErrorUpdatedAt]
  );

  const portSuccess = React.useCallback(() => {
    setDataUpdatedAt((previous) => {
      if (Date.now() > previous) {
        return Date.now();
      } else {
        return previous;
      }
    });
  }, [setDataUpdatedAt]);

  const useMutationOrQueryOnSuccess = React.useCallback(() => {
    dataSourceMyBagTotals_invalidate();
  }, [dataSourceMyBagTotals_invalidate]);

  const ignoreMyCurrentStateFactory: UseGenericDataPortForMyBagController["ignoreMyCurrentStateFactory"] =
    React.useCallback(
      (mutation: ReactQuery.UseMutationResult<any, any, any, any>) =>
        React.useCallback(() => {
          let shouldIgnore = false;

          if (mutation.error) {
            const errorMessage = (mutation.error as Error).message;
            if (errorMessage) {
              const isThisAnOutOfStockError =
                errorMessage.includes(OUT_STOCK_ERROR) ||
                errorMessage.includes(OUT_STOCK_UPDATE) ||
                errorMessage.includes(OUT_OF_STOCK_GENERIC_STARTING);

              if (!isThisAnOutOfStockError) {
                shouldIgnore = true;
              }
            } else {
              shouldIgnore = true;
            }
          }

          return shouldIgnore;
        }, [mutation, mutation.error]),
      []
    );

  const value: UseGenericDataPortForMyBagConsumer = React.useMemo(
    () => ({
      dataUpdatedAt,
      errorUpdatedAt,
    }),
    [dataUpdatedAt, errorUpdatedAt]
  );

  const controller: UseGenericDataPortForMyBagController = React.useMemo(
    () => ({
      portError,
      portSuccess,
      useMutationOrQueryOnSuccess,
      ignoreMyCurrentStateFactory,
    }),
    [
      portError,
      portSuccess,
      useMutationOrQueryOnSuccess,
      ignoreMyCurrentStateFactory,
    ]
  );

  return [value, controller];
};
